import React from 'react';
import { formatNumber, formatMoney } from '../../shared/numberFormat';
import { formatDimensions } from '../../shared/useShipmentWeightUnit';
import { formatQty, allPackages, actualPostageOf, useDefaultTaxRate } from './packageShared';

// Same outlined button as a package's on Pack (PackagesPanel.js), so the
// actions are easy to see and look alike on every screen.
const quietButton = { padding: '6px 12px', borderRadius: 6, border: '1px solid #bfc8d6', background: '#fff', color: '#333', fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' };

// A shipment's packages (issues #299, #313): each outermost package's
// packaging, dimensions and weight first, then its own goods and the
// packages inside it beneath, at any depth. Each package's label (issues
// #300, #445) can be printed again from here, at a packing station (issue #305) — onLabel is null elsewhere —
// and, beneath them, the shipment's Sender Labels (issue #451), likewise. Contents
// are read-only; with onDetails each package gets a Details link, and
// editor(p) renders under it whatever its details editor is (null when it
// isn't open).
// Each package shows its Packaging Cost (issue #314) when its Packaging has
// one. Each outermost package shows its Actual Postage (issue #350), inc
// and ex GST, or that it isn't entered yet. The Shipment Cost follows —
// postage plus every package's Packaging Cost — against the Shipping
// Charged on the order (`shippingCharge`, ex GST), and whether that over-
// or under-recovered, once every outermost package's postage is entered.
const mutedStyle = { fontSize: 12, color: '#888' };

const withInc = (exAmount, taxRate) =>
  taxRate !== null && taxRate !== undefined ? `${formatMoney(Number(exAmount) * (1 + Number(taxRate)))} inc GST · ` : '';

function ActualPostage({ p, taxRate }) {
  const postage = actualPostageOf(p);
  if (postage === null) return <div style={mutedStyle}>Actual postage not yet entered</div>;
  return (
    <div style={{ fontSize: 12, color: '#333' }}>
      Actual Postage <span style={{ fontWeight: 600 }}>{withInc(postage, taxRate)}{formatMoney(postage)} ex GST</span>
    </div>
  );
}

function ShipmentPackages({ packages, weightUnit, onLabel, onSenderLabels, onDetails, editor, shippingCharge }) {
  const taxRate = useDefaultTaxRate();
  if (!packages || !packages.length) return <div style={{ fontSize: 13, color: '#888' }}>No packages recorded</div>;
  const renderOne = (p, nested) => {
    const details = [
      p.packaging?.name,
      formatDimensions(p),
      p.weight !== null && p.weight !== undefined ? `${formatNumber(p.weight, { maxDecimals: 3 })}${weightUnit ? ` ${weightUnit}` : ''}` : null,
      p.packaging_cost !== null && p.packaging_cost !== undefined ? `Packaging Cost ${formatMoney(p.packaging_cost)}` : null,
    ].filter(Boolean).join(' · ');
    const goods = p.goods || [];
    const inside = p.packages || [];
    return (
      <div key={p.id} style={nested ? { borderTop: '1px solid #e3e7ee', marginTop: 6, paddingTop: 6, marginLeft: 10 } : { padding: '8px 10px', borderRadius: 6, background: '#f6f8fa', marginBottom: 6 }}>
        {/* A long Packaging name wraps; the buttons keep their size and drop
            under it when the row's too narrow for both. */}
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 600, flex: '1 1 140px', minWidth: 0 }}>{p.name}</span>
          <span style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
            {onDetails && (
              <button type="button" title={nested ? "Edit this package's packaging, dimensions and weight" : "Edit this package's packaging, dimensions, weight and actual postage"} onClick={() => onDetails(p)} style={quietButton}>
                Details
              </button>
            )}
            {onLabel && (
              <button type="button" onClick={() => onLabel(p.id)} style={quietButton}>
                Print Label
              </button>
            )}
          </span>
        </div>
        {details && <div style={{ fontSize: 12, color: '#666' }}>{details}</div>}
        {!nested && <ActualPostage p={p} taxRate={taxRate} />}
        {editor && editor(p)}
        {goods.length === 0 && inside.length === 0 && <div style={{ fontSize: 12, color: '#888' }}>No contents recorded</div>}
        {goods.map((c) => (
          <div key={c.line_id} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12 }}>
            <span>{c.sku} <span style={{ color: '#666' }}>{c.name}</span></span>
            <span style={{ whiteSpace: 'nowrap' }}>{formatQty(c.qty)}{c.uom ? ` ${c.uom}` : ''}</span>
          </div>
        ))}
        {inside.map((inner) => renderOne(inner, true))}
      </div>
    );
  };
  const packagingCost = allPackages(packages).reduce((sum, p) => sum + (Number(p.packaging_cost) || 0), 0);
  const postages = packages.map(actualPostageOf);
  const notEntered = postages.filter((c) => c === null).length;
  const postage = postages.reduce((sum, c) => sum + (c || 0), 0);
  const shipmentCost = postage + packagingCost;
  const charged = shippingCharge !== null && shippingCharge !== undefined ? Number(shippingCharge) : null;
  const difference = charged !== null && !notEntered ? Math.round((charged - shipmentCost) * 100) / 100 : null;
  const totalRow = { display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '2px 10px' };
  const ruled = { borderTop: '1px solid #b3bbc7', marginTop: 2, paddingTop: 4 };
  // Shipment Cost (issue #350): Actual Postage plus Packaging Cost, against
  // the Shipping Charged (its Invoice's once invoiced, else the order's), and whether that over- or
  // under-recovered — only once every outermost package's postage is in.
  return (
    <>
      {packages.map((p) => renderOne(p, false))}
      {onSenderLabels && (
        <div style={{ marginBottom: 6 }}>
          <button type="button" title="Print a Sender Label for each Parcel in this shipment" onClick={onSenderLabels} style={quietButton}>
            Print Sender Labels
          </button>
        </div>
      )}
      <div style={{ fontSize: 12, fontWeight: 600, color: '#5a6b85', textTransform: 'uppercase', padding: '6px 10px 2px' }}>Shipment Cost (ex GST)</div>
      <div style={totalRow}>
        <span>Actual Postage{notEntered ? <span style={{ color: '#888' }}> — {notEntered} not yet entered</span> : null}</span>
        <span>{notEntered ? '—' : formatMoney(postage)}</span>
      </div>
      <div style={totalRow}>
        <span>Packaging Cost</span>
        <span>{formatMoney(packagingCost)}</span>
      </div>
      <div style={{ ...totalRow, ...ruled, fontWeight: 600 }}>
        <span>Total Cost</span>
        <span>{notEntered ? '—' : formatMoney(shipmentCost)}</span>
      </div>
      <div style={{ ...totalRow, marginTop: 6 }}>
        <span>Shipping Charged</span>
        <span>{charged !== null ? formatMoney(charged) : '—'}</span>
      </div>
      {difference !== null && (
        <div style={{ ...totalRow, ...ruled, fontWeight: 600, color: difference < 0 ? '#b91c1c' : '#2e7d32' }}>
          <span>{difference < 0 ? 'Under-recovered' : 'Over-recovered'}</span>
          <span>{difference < 0 ? '−' : '+'}{formatMoney(Math.abs(difference))}</span>
        </div>
      )}
    </>
  );
}

export default ShipmentPackages;
